"use client";

import {
  useEffect,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  AppPlaceholder,
  Text,
  useSitecore,
} from "@sitecore-content-sdk/nextjs";

import { useAccordionContext } from "contexts/AccordionContext";
import { AccordionItemProps } from "./accordion.props";

export const Default = (props: AccordionItemProps): JSX.Element | null => {
  console.log("---->[AccordionItem] Component", props.fields);

  const { page, fields, params, rendering } = props;
  const isPageEditing = page.mode.isEditing;
  const { componentMap } = useSitecore();
  const { activeIndex, setActiveIndex, registerFirstItem } =
    useAccordionContext();

  const index = rendering?.params?.DynamicPlaceholderId
    ? Number.parseInt(rendering?.params?.DynamicPlaceholderId, 10)
    : 0;

  useEffect(() => {
    if (index) {
      registerFirstItem(index);
    }
  }, [index, registerFirstItem]);

  const isExpanded = activeIndex === index;
  const onToggle = () => setActiveIndex(isExpanded ? -1 : index);

  const id = params?.RenderingIdentifier;
  const phKey = `accordionbody-${rendering?.params?.DynamicPlaceholderId}`;
  const visibilityClass = isPageEditing ? "hidden" : "invisible";

  const accordionTitle = fields?.AccordionTitle;
  const headingStyle = isExpanded
    ? "rounded-t-[8px] text-white bg-ecolab-blue bg-[position:center_right_32px] bg-icon-accordion-minus"
    : "rounded-[8px] text-ecolab-blue bg-white bg-[position:center_right_25px] bg-icon-accordion-plus shadow-[var(--shadow-accordianbody)]";
  const bodyUlStyle = isExpanded
    ? "transition-all duration-300 ease-in-out max-h-[3000px] overflow-y-auto visible p-5 bg-white shadow-[var(--shadow-accordianbody)] rounded-b-[8px]"
    : `transition-all duration-300 ease-in-out max-h-0 overflow-hidden ${
        visibilityClass
      }`;
  const headingAreaLabel = isExpanded
    ? `${accordionTitle?.value} expanded`
    : `${accordionTitle?.value} collapsed`;

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onToggle();
    }
  };

  if (!accordionTitle?.value) {
    return null;
  }

  return (
    <div
      className={`accordion-item-component ${page?.mode?.isEditing ? "mb-[10px]" : ""}`}
      id={id || undefined}
      data-testid="accordion-item-component"
      data-role="accordion-item-component"
    >
      <div className="component-content">
        <div
          className={`accordion-heading text-xl font-medium cursor-pointer py-[15px] pl-[20px] pr-[63px] lg:px-[40px] lg:py-[30px] bg-no-repeat bg-size-[40px 40px] ${headingStyle}`}
          onClick={onToggle}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-label={headingAreaLabel}
          data-testid="accordion-heading"
        >
          <Text
            tag="h4"
            field={accordionTitle}
            className="font-medium text-xl tracking-[0.4px] leading-[30px] lg:tracking-[0.8px] lg:leading-[20px] m-0 uppercase"
            data-testid="header-title"
          />
        </div>
        <div
          className={`accordion-body mb-[10px] ${bodyUlStyle}`}
          data-testid="accordion-body"
        >
          <AppPlaceholder
            name={phKey}
            rendering={rendering}
            page={page}
            componentMap={componentMap}
          />
        </div>
      </div>
    </div>
  );
};
